import { API, deleteRequest, getRequest, getRequestBlob, postRequest, putRequest } from '@/api/api';
import { generateSearchParams } from '@/utils/generate-search-params';

import type {
  CreateDigitalNavigationLogFormValues,
  DigitalNavigationLog,
  DigitalNavigationLogResponseDto,
  DigitalNavigationLogsList,
  DigitalNavigationLogsListDto,
  UpdateDigitalNavigationLogFormValues,
} from './digital-navigation-log.dto';
import {
  mapCreateDigitalNavigationLogDto,
  mapDigitalNavigationLog,
  mapUpdateDigitalNavigationLogDto,
} from './digital-navigation-log.mappers';

export const getDigitalNavigationLogs = async (
  limit: number,
  page: number,
): Promise<DigitalNavigationLogsList> => {
  const searchParams = generateSearchParams({ limit, page });
  const { data } = await getRequest<DigitalNavigationLogsListDto>(
    API.VESSEL,
    `/digital-navigation-log?${searchParams}`,
  );
  return {
    count: data.count,
    items: data.items.map(mapDigitalNavigationLog),
  };
};

export const getDigitalNavigationLogById = async (id: string): Promise<DigitalNavigationLog> => {
  const { data } = await getRequest<DigitalNavigationLogResponseDto>(
    API.VESSEL,
    `/digital-navigation-log/${id}`,
  );
  return mapDigitalNavigationLog(data);
};

const toRfc3339 = (date: string): string => {
  const d = new Date(date.length === 10 ? `${date}T00:00:00.000Z` : date);
  return Number.isNaN(d.getTime()) ? date : d.toISOString();
};

export const getDigitalNavigationLogByDate = async (
  date: string,
): Promise<DigitalNavigationLog | undefined> => {
  const { data } = await getRequest<DigitalNavigationLogResponseDto | undefined>(
    API.VESSEL,
    `/digital-navigation-log/date`,
    false,
    { date: toRfc3339(date) },
  );
  return data ? mapDigitalNavigationLog(data) : undefined;
};

export const createDigitalNavigationLog = async (
  values: CreateDigitalNavigationLogFormValues,
): Promise<DigitalNavigationLog> => {
  const { data } = await postRequest<DigitalNavigationLogResponseDto, unknown>(
    API.VESSEL,
    `/digital-navigation-log`,
    mapCreateDigitalNavigationLogDto(values),
  );
  return mapDigitalNavigationLog(data);
};

export const updateDigitalNavigationLog = async (
  values: UpdateDigitalNavigationLogFormValues,
): Promise<DigitalNavigationLog> => {
  const { data } = await putRequest<DigitalNavigationLogResponseDto, unknown>(
    API.VESSEL,
    `/digital-navigation-log/${values.id}`,
    mapUpdateDigitalNavigationLogDto(values),
  );
  return mapDigitalNavigationLog(data);
};

export const deleteDigitalNavigationLog = async (id: string): Promise<void> => {
  await deleteRequest(API.VESSEL, `/digital-navigation-log/${id}`);
};

export const getDigitalNavigationLogPdf = async (id: string, timezone?: string): Promise<Blob> => {
  return getRequestBlob(API.VESSEL, `/digital-navigation-log/${id}/pdf`, { timezone });
};
